'use client';

import React, { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface DonationOption {
  id: string;
  amount: number;
  impact: string;
  popular?: boolean;
}

interface DonationCardProps {
  onDonateClick: (amount: number) => void;
}

const DonationCard: React.FC<DonationCardProps> = ({ onDonateClick }) => {
  const [selectedAmount, setSelectedAmount] = useState<number | null>(null);
  const [customAmount, setCustomAmount] = useState<string>('');

  const donationOptions: DonationOption[] = [
    { id: '500', amount: 500, impact: 'Provides basic health checkup for 1 person' },
    { id: '1000', amount: 1000, impact: 'Supports 2 families with medical assistance', popular: true },
    { id: '2500', amount: 2500, impact: 'Funds complete health camp for 10 people' },
    { id: '5000', amount: 5000, impact: 'Enables comprehensive care for 5 families' },
  ];

  const handleAmountSelect = (amount: number) => {
    setSelectedAmount(amount);
    setCustomAmount('');
  };

  const handleCustomAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, '');
    setCustomAmount(value);
    if (value) {
      setSelectedAmount(parseInt(value));
    } else {
      setSelectedAmount(null);
    }
  };

  const handleDonateClick = () => {
    if (selectedAmount && selectedAmount > 0) {
      onDonateClick(selectedAmount);
    }
  };

  return (
    <div className="bg-card rounded-xl shadow-elevated p-6 lg:p-8">
      <div className="flex items-center space-x-3 mb-6">
        <div className="w-12 h-12 bg-accent/10 rounded-lg flex items-center justify-center">
          <Icon name="HeartIcon" size={24} className="text-accent" variant="solid" />
        </div>
        <div>
          <h3 className="text-xl font-heading font-bold text-foreground">Make a Donation</h3>
          <p className="text-sm text-muted-foreground">Your contribution creates lasting impact</p>
        </div>
      </div>

      <div className="space-y-4 mb-6">
        <div className="grid grid-cols-2 gap-3">
          {donationOptions.map((option) => (
            <button
              key={option.id}
              onClick={() => handleAmountSelect(option.amount)}
              className={`relative p-4 rounded-lg border-2 transition-all duration-300 text-left ${
                selectedAmount === option.amount
                  ? 'border-accent bg-accent/5 shadow-subtle'
                  : 'border-border hover:border-accent/50 hover:bg-muted'
              }`}
            >
              {option.popular && (
                <span className="absolute -top-2 -right-2 bg-accent text-accent-foreground text-xs font-semibold px-2 py-1 rounded-full">
                  Popular
                </span>
              )}
              <div className="text-2xl font-heading font-bold text-foreground mb-1">
                ₹{option.amount.toLocaleString('en-IN')}
              </div>
              <div className="text-xs text-muted-foreground line-clamp-2">{option.impact}</div>
            </button>
          ))}
        </div>

        <div className="relative">
          <input
            type="text"
            value={customAmount}
            onChange={handleCustomAmountChange}
            placeholder="Enter custom amount"
            className="w-full px-4 py-3 pl-8 border-2 border-border rounded-lg focus:border-accent focus:outline-none transition-colors duration-300"
          />
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground font-semibold">
            ₹
          </span>
        </div>
      </div>

      <div className="bg-muted rounded-lg p-4 mb-6">
        <div className="flex items-start space-x-3">
          <Icon name="InformationCircleIcon" size={20} className="text-primary mt-0.5" />
          <div className="flex-1">
            <p className="text-sm text-foreground font-medium mb-1">Tax Benefits Available</p>
            <p className="text-xs text-muted-foreground">
              Donations are eligible for 80G tax deduction. You'll receive a certificate via email.
            </p>
          </div>
        </div>
      </div>

      <button
        onClick={handleDonateClick}
        disabled={!selectedAmount || selectedAmount <= 0}
        className="w-full bg-accent text-accent-foreground font-heading font-semibold py-4 rounded-lg hover:bg-accent/90 transition-all duration-300 shadow-subtle hover:shadow-cta disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
      >
        <span>Proceed to Donate</span>
        <Icon name="ArrowRightIcon" size={20} />
      </button>

      <p className="text-xs text-center text-muted-foreground mt-4">
        Secure payment powered by trusted payment gateways
      </p>
    </div>
  );
};

export default DonationCard;